import {
  ComboboxItem,
  Stack,
  Text,
  FileButton,
  Button as MantineButton,
  Tooltip,
} from '@mantine/core';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { v4 as uuidv4 } from 'uuid';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { EntityWithFile, UploadedFile } from '../hooks/file-uploads/types';
import { useFileUploads } from '../hooks/file-uploads/useFileUploads';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import FilesTable from './files-table/FilesTable';
import FileStatusPill from './FileStatusPill';
import React, { useEffect, useMemo, useState } from 'react';
import { useDetectedEntitiesStore } from '../stores/useDetectedEntitiesStore';
import { DropZone } from './drop-zone/DropZone';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';
import useDataUploadModels from 'src/data-capture/hooks/templates/useDataUploadModels';
import { Button } from 'src/components';
import {
  FileWithTemplate,
  UploadFilesModal,
} from 'src/data-capture/file-management/drop-zone/UploadFilesModal';
import { dataUploadFileProperties, Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { useNavigate } from 'react-router';
import { IconArrowRight, IconFolder, IconPlus } from '@tabler/icons-react';
import MoveFileModal from './files-table/MoveFileModal';
import { useDisclosure } from '@mantine/hooks';
import FileUploadLayout from './FileUploadLayout';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import EmptyView from './components/EmptyView';
import { useOneDriveFileUpload } from '../hooks/file-uploads/useOneDriveFileUpload';
import { useSharePointPicker } from 'src/data-capture/file-management/drop-zone/SharePointProvider';
import { Logger } from 'src/utils/Logger';
import { ApiError } from 'src/error/ApiError';
import { ALLOWED_EXTENSIONS, FILE_BUTTON_ACCEPT } from './allowedFileTypes';

export function AddDocumentsPage() {
  const { clientId } = useCurrentClient();
  const { openPicker, pendingPickedItems, clearPendingPickedItems } = useSharePointPicker();
  const clearExpiredFiles = useDetectedEntitiesStore(state => state.clearExpiredFiles);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedOneDriveItems, setSelectedOneDriveItems] = useState<PickedItem[]>([]);
  const [selectedFileForMove, setSelectedFileForMove] = useState<UploadedFile | null>(null);
  const [moveModalOpened, { open: openMoveModal, close: closeMoveModal }] = useDisclosure(false);
  const [uploadModalOpened, { open: openUploadModal, close: closeUploadModal }] =
    useDisclosure(false);
  const [
    oneDriveUploadModalOpened,
    { open: openOneDriveUploadModal, close: closeOneDriveUploadModal },
  ] = useDisclosure(false);

  useEffect(() => {
    if (pendingPickedItems && pendingPickedItems.length > 0) {
      Logger.info('Processing pending OneDrive items from global provider', { pendingPickedItems });
      showOneDriveUploadModal(pendingPickedItems);
      clearPendingPickedItems();
    }
  }, [pendingPickedItems, clearPendingPickedItems]);

  const selectData = [
    { value: 'BANK_STATEMENT', label: 'Bank statement' },
    { value: 'LETTER_OF_CORRESPONDENCE', label: 'Letter of correspondence' },
    { value: 'UTILITY_BILL', label: 'Utility bill' },
    { value: 'FACT_FIND', label: 'Fact find' },
    { value: 'IDENTIFICATION_DOCUMENT', label: 'Identification document' },
    { value: 'MEETING_NOTES', label: 'Meeting notes' },
    { value: 'AUDIO', label: 'Audio' },
  ];
  const [documentTypeChoice] = useState<ComboboxItem>(selectData[0]);

  useEffect(() => {
    clearExpiredFiles();
  }, []);

  const {
    filesToAdd,
    deleteFile,
    setFileResolved,
    refetchFiles,
    moveFile,
    isLoading: isLoadingFiles,
    error,
  } = useFileUploads({
    autoRefreshProcessing: true,
  });
  const isLoading = isLoadingFiles;

  const { files: models } = useDataUploadModels();
  const { uploadFiles } = useFileUploads();
  const { uploadOneDriveFiles } = useOneDriveFileUpload();

  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();

  const handleDeleteFile = async (file: EntityWithFile) => {
    try {
      await deleteFile(file.id);
    } catch (error) {
      notifyAndLogError(
        'Failed to delete file',
        'An error occurred while trying to delete the file. Please try again later.',
        error,
        { clientId, fileId: file.id },
      );
    }
  };

  const handleSetAsResolved = async (file: UploadedFile, isResolved: boolean = true) => {
    try {
      Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_SET_RESOLVED, dataUploadFileProperties(file));
      await setFileResolved(file, isResolved);
    } catch (error) {
      notifyAndLogError(
        'Failed to mark file as reviewed',
        'An error occurred while trying to mark the file as reviewed. Please try again later.',
        error,
        { clientId, fileId: file.id },
      );
    }
  };

  const handleViewFile = async (uploadedFile: UploadedFile, anchor?: HTMLAnchorElement) => {
    try {
      if (anchor) {
        const link = await createLink(uploadedFile.file.id);
        anchor.href = link.url;
        anchor.download = uploadedFile.file.fileName || 'download';
        anchor.target = '_blank';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } else {
        const link = await createLink(uploadedFile.file.id);
        window.open(link.url, '_blank');
      }
    } catch (error) {
      notifyAndLogError(
        'Failed to open file',
        'An error occurred while trying to open the file. Please try again later.',
        error,
        { clientId, fileId: uploadedFile.file.id },
      );
    }
  };

  const showUploadModal = (files: File[]) => {
    setSelectedFiles(files);
    closeOneDriveUploadModal();
    openUploadModal();
  };

  const showOneDriveUploadModal = (items: PickedItem[]) => {
    setSelectedOneDriveItems(items);
    openOneDriveUploadModal();
  };

  const handleOneDriveClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    openPicker(handlePickedOneDriveItems, ALLOWED_EXTENSIONS);
  };

  const handleOneDriveFilesUpload = async (
    oneDriveFilesWithTemplate: FileWithTemplate<PickedItem>[],
  ) => {
    try {
      const { modelId, documentDate } = oneDriveFilesWithTemplate[0] || {};

      if (!modelId) {
        throw new Error('Template selection is required for file upload');
      }

      const items = oneDriveFilesWithTemplate.map(f => f.file);
      const uploadedFiles = await uploadOneDriveFiles({
        items,
        modelId,
        documentDate: documentDate || null,
        parentId: null,
      });
      await handleFilesUploaded(uploadedFiles);
      closeOneDriveUploadModal();
    } catch (error) {
      if (!ApiError.isGloballyHandledError(error)) {
        await handleFailedUpload(oneDriveFilesWithTemplate.length, error);
      }
    }
  };

  const handleFilesUpload = async (filesWithTemplate: FileWithTemplate<File>[]) => {
    try {
      const fileUploadInputs = filesWithTemplate.map(({ file, modelId, documentDate }) => ({
        file,
        modelId,
        documentDate,
      }));

      const uploadedFiled = await uploadFiles(fileUploadInputs);
      await handleFilesUploaded(uploadedFiled.map(f => f.data));
      closeUploadModal();
    } catch (error) {
      if (!ApiError.isGloballyHandledError(error)) {
        await handleFailedUpload(filesWithTemplate.length, error);
      }
    }
  };

  const handleFailedUpload = async (fileCount: number, uploadError: unknown) => {
    notifyAndLogError(
      'Failed to upload files',
      'An error occurred while uploading the files. Please try again later.',
      uploadError,
      { clientId, fileCount: fileCount },
    );
    await refetchFiles();
    navigate(`/client/${clientId}/document/add`);
  };

  const handleFilesUploaded = async (uploadedFiles: UploadedFile[]) => {
    try {
      uploadedFiles.forEach(file => {
        Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_UPLOADED, dataUploadFileProperties(file));
      });

      dispatchAppEvent('DATA_UPLOADED');
      await refetchFiles();
      navigate(`/client/${clientId}/document/add`);
    } catch (error) {
      notifyAndLogError(
        'Failed to process uploaded files',
        'An error occurred while processing the uploaded files. Please try again later.',
        error,
        { clientId, fileCount: uploadedFiles.length },
      );
    }
  };

  const handleFileDocument = (file: UploadedFile) => {
    setSelectedFileForMove(file);
    openMoveModal();
  };

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_DOCUMENT_FILED, {
        ...dataUploadFileProperties(file),
        parentId,
      });
    } catch (error) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        error,
        { clientId, fileId: file.id, parentId },
      );
    }
  };

  const filteredFiles = useMemo(() => {
    if (!searchValue) return filesToAdd;
    return filesToAdd.filter(file =>
      file.file.fileName.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [filesToAdd, searchValue]);

  const getPrimaryAction = (file: UploadedFile) => {
    if (!file.isResolved) {
      return {
        type: 'link' as const,
        label: 'Process data',
        variant: 'primary' as const,
        icon: IconArrowRight,
        onClick: () => {
          Mixpanel.track(
            MixpanelEvents.DATA_UPLOAD_FILE_PROCESS_CLICK,
            dataUploadFileProperties(file),
          );
        },
        getLink: (file: UploadedFile) =>
          `/client/${clientId}/detected-entities?fileId=${file.id}&fileName=${file.file.fileName}&guid=${uuidv4()}`,
        condition: (file: UploadedFile) =>
          !file.backgroundProcess || file.backgroundProcess.status === 'SUCCESS',
      };
    }

    if (file.isResolved && !file.file.isFiled) {
      return {
        type: 'action' as const,
        label: 'File document',
        variant: 'secondary' as const,
        icon: IconFolder,
        onClick: handleFileDocument,
      };
    }

    return undefined;
  };

  if (error || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  const handlePickedOneDriveItems = (items: PickedItem[]) => {
    Logger.info('picked items from OneDrive:', { items });
    showOneDriveUploadModal(items);
  };

  return (
    <>
      {filteredFiles.length === 0 ? (
        <FileUploadLayout title="Add documents">
          <>
            <EmptyView
              title="Keep all documents securely and in one place"
              subtitle="Siesta AI will scan and look for data that you use to populate the fields on the system. Documents can be used for Siesta AI reports either for context and/or as information for including."
              ctaText="Upload new"
              ctaIcon={IconPlus}
              customCta={
                <Stack gap="xxs">
                  <FileButton onChange={showUploadModal} multiple accept={FILE_BUTTON_ACCEPT}>
                    {props => (
                      <MantineButton {...props} variant="primary" size="md">
                        Upload new
                      </MantineButton>
                    )}
                  </FileButton>
                  <Button onClick={handleOneDriveClick} variant="secondary" size="md">
                    Upload from SharePoint
                  </Button>
                </Stack>
              }
            />
          </>
        </FileUploadLayout>
      ) : (
        <FileUploadLayout
          title="Add documents"
          showSearch
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        >
          <Stack gap="xxl">
            <DropZone
              onFilesSelected={showUploadModal}
              onOneDriveClick={handleOneDriveClick}
              documentType={documentTypeChoice}
            />

            <FilesTable<UploadedFile>
              entitiesWithFile={filteredFiles}
              isLoading={isLoading}
              primaryAction={getPrimaryAction}
              additionalColumns={[
                {
                  label: 'AI model',
                  valueBuilder: f => (
                    <Text fw={400} size="sm">
                      {f.dataUploadModel?.shortName ?? f.dataUploadModel?.displayName}
                    </Text>
                  ),
                },
                {
                  label: 'Status',
                  valueBuilder: f => {
                    if (f.isResolved && !f.file.isFiled) {
                      return <FileStatusPill status="UNFILED" />;
                    }
                    if (!f.backgroundProcess?.status) {
                      return <></>;
                    }
                    if (f.backgroundProcess.status === 'FAILED') {
                      const errorMessage =
                        (f.backgroundProcess.extraDetails?.exc_info as string) ||
                        'Processing failed';
                      return (
                        <Tooltip label={errorMessage} multiline maw={400}>
                          <span>
                            <FileStatusPill status={f.backgroundProcess.status} />
                          </span>
                        </Tooltip>
                      );
                    }
                    return <FileStatusPill status={f.backgroundProcess.status} />;
                  },
                },
              ]}
              actions={(file: UploadedFile) => {
                if (file.isResolved) {
                  return [
                    { label: 'Download document', action: handleViewFile },
                    { type: 'divider' },
                    {
                      label: 'Set as not reviewed',
                      action: file => handleSetAsResolved(file, false),
                    },
                    { label: 'Delete', action: handleDeleteFile },
                  ];
                } else {
                  return [
                    { label: 'Download document', action: handleViewFile },
                    { type: 'divider' },
                    { label: 'Set as reviewed', action: file => handleSetAsResolved(file, true) },
                    { label: 'Delete', action: handleDeleteFile },
                  ];
                }
              }}
            />
          </Stack>
        </FileUploadLayout>
      )}

      <UploadFilesModal
        opened={uploadModalOpened}
        close={closeUploadModal}
        files={selectedFiles}
        dataUploadModels={models}
        onSubmit={handleFilesUpload}
      />

      <UploadFilesModal
        opened={oneDriveUploadModalOpened}
        close={closeOneDriveUploadModal}
        files={selectedOneDriveItems}
        dataUploadModels={models}
        onSubmit={handleOneDriveFilesUpload}
        defaultDocumentDate={
          selectedOneDriveItems.length === 1 && selectedOneDriveItems[0].createdDateTime
            ? selectedOneDriveItems[0].createdDateTime
            : null
        }
      />

      {selectedFileForMove && (
        <MoveFileModal
          opened={moveModalOpened}
          close={closeMoveModal}
          file={selectedFileForMove}
          onFileMoved={handleMoveFile}
        />
      )}
    </>
  );
}
